import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import ApplicationFooter from '@frolight/components/ApplicationFooter';
import GatewayRegistrationConfirmation from '@frolight/components/GatewayRegistrationConfirmation';
import SignInForm from '@frolight/components/SignInForm';
import Tabs from '@frolight/components/Tabs';
import UserRegistrationForm from '@frolight/components/UserRegistrationForm';

import useAuth from '@frolight/hooks/useAuth';
import useAuthenticatedAPIServiceCall from '@frolight/hooks/useAuthenticatedAPIServiceCall';
import usePageTitle from '@frolight/hooks/usePageTitle';

import useGatewayData from './hooks/useGatewayData';

import * as gatewayRegistrationRequestService from '@frolight/services/api/gatewayRegistrationRequestService';
import * as organisationService from '@frolight/services/api/organisationService';
import LocaleSelector from '@frolight/components/LocaleSelector';
import useLocalizedFormattedText from '@frolight/hooks/useLocalizedFormattedText';
import i18next from '@frolight/i18n';

const DeviceRegistration = () => {
  const { lftext } = useLocalizedFormattedText();

  const { auth } = useAuth();
  const authenticatedAPIServiceCall = useAuthenticatedAPIServiceCall();

  const [ loading, setLoading ] = useState(true);
  const [ organisationData, setOrganisationData ] = useState(null);
  const [ newUserId, setNewUserId ] = useState(null);
  const [ signUpStatus, setSignUpStatus ] = useState({ hasSignedUp: false });
  const [ gatewayRegistrationComplete, setGatewayRegistrationComplete ] = useState(false);

  const { registrationCode } = useParams();

  const { loading: gatewayDataLoading, error: gatewayDataError, data: gatewayData } = useGatewayData(registrationCode);

  usePageTitle(lftext('pages.device_registration.page_title'));

  useEffect(() => {
    let mounted = true;

    if (gatewayDataError) {
      mounted && setLoading(false);
    }

    return () => {
      mounted = false;
    }
  }, [ gatewayDataError ]);

  useEffect(() => {
    let mounted = true;
    const fetchOrganisationsAbortController = new AbortController();

    const fetchOrganisations = async () => {
      setLoading(true);
      try {
        const fetchedOrganisations = await authenticatedAPIServiceCall(
          organisationService.getOrganisations,
          {
            abortController: fetchOrganisationsAbortController
          }
        );
        if (fetchedOrganisations.data) {
          if (mounted) setOrganisationData(fetchedOrganisations.data);
        }
      } catch (err) {
        console.error('Error getting organisations', err);
        if (mounted) setLoading(false);
      }
    };

    if (gatewayData) {
      if (auth.user) {
        fetchOrganisations();
      } else {
        if (mounted) setLoading(false);
      }
    }

    return () => {
      mounted = false;
      fetchOrganisationsAbortController.abort();
    }
  }, [auth, gatewayData]);

  useEffect(() => {
    let mounted = true;
    if (organisationData) {
      if (mounted) setLoading(false);
    }

    return () => {
      mounted = false;
    }
  }, [organisationData])
  
  useEffect(() => {
    let mounted = true;
    const createRegistrationRequestAbortController = new AbortController();

    const createRegistrationRequest = async() => {
      setLoading(true);
      try {
        await gatewayRegistrationRequestService.createGatewayRegistrationRequest(
          createRegistrationRequestAbortController, 
          {
            gatewayId: gatewayData.id,
            userId: signUpStatus.newUserInformation.id
          }
        );
        if (mounted) setNewUserId(signUpStatus.newUserInformation.id);
      } catch (err) {
        console.error('Error creating registration request', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (signUpStatus.hasSignedUp) {
      createRegistrationRequest();
    }

    return () => {
      mounted = false;
      createRegistrationRequestAbortController.abort();
    }
  }, [ signUpStatus, gatewayData ]);

  const onSignUpSuccess = (newUserInformation) => {
    setSignUpStatus({
      hasSignedUp: true,
      newUserInformation: newUserInformation
    });
  };

  const onSignInSuccess = () => {
    
  }

  const onGatewayRegistrationComplete = () => {
    setGatewayRegistrationComplete(true);
  };

  const renderCorrectForm = () => {
    if (loading) {
      return (
        <div className="loader">
          <p>Loading...</p>
        </div>
      );
    }
    if (signUpStatus.hasSignedUp && newUserId) {
      return (
        <div className="rich_text">
          {lftext('pages.device_registration.confirm_email')}
        </div>
      );
    }
    if (organisationData && gatewayRegistrationComplete) {
      return (
        <div className="rich_text">
          {lftext('pages.device_registration.confirm_registration', {organisationName: organisationData[0].name})}
        </div>
      )
    }
    if (gatewayDataError) {
      return (
        <div className="rich_text">
          <div className="feedback_message  feedback_message--error">
              {lftext('pages.device_registration.device_not_found.error', { registrationCode })}
            <ul>
              <li>{lftext('pages.device_registration.device_not_found.reasons.does_not_exist')}</li>
              <li>{lftext('pages.device_registration.device_not_found.reasons.already_registered')}</li>
            </ul>
            <p>
              {lftext('pages.device_registration.device_not_found.message')}
            </p>
          </div>
        </div>
      )
    }
    if (auth.user) {
      if (organisationData) {
        if (organisationData.length === 1) {
          return (
            <GatewayRegistrationConfirmation
              gateways={[gatewayData]}
              organisation={ organisationData[0] }
              onRegistrationComplete={ onGatewayRegistrationComplete }
            />
          );
        } else if (organisationData.length > 1) {
          return (
            <div className="rich_text">
              {lftext('pages.device_registration.organisation_errors.multiple_organisations')}
            </div>
          );
        } else if (organisationData.length === 0) {
          return (
            <div className="rich_text">
              {lftext('pages.device_registration.organisation_errors.no_organisation')}
            </div>
          );
        }
      }
    }
    return (
      <>
        <h1>{lftext('pages.device_registration.title')}</h1>
          {lftext('pages.device_registration.description')}
        <Tabs
          tabs={[
            {
              label: lftext('pages.device_registration.tab_register'),
              element: <UserRegistrationForm onSignUpSuccess={ onSignUpSuccess } />
            },
            {
              label: lftext('pages.device_registration.tab_sign_in'),
              element: <SignInForm onSignInSuccess={ onSignInSuccess } />
            }
          ]}
        />
      </>
    );
  };
  return (
    <div className="simple_page">
      <div className="simple_page__inner">
      <LocaleSelector theme='dark' />
        <section className="panel">
          <div className="panel__header">
            <header className="small_application_header">
              <Link className="logo_link  logo_link--large" to="/">
                <img className="logo_link__img" src="images/frolight_logo.svg" alt="Frolight logo" />
              </Link>
            </header>
          </div>
          <div className="panel__content">
            <main>
              { renderCorrectForm() }
            </main>
          </div>
        </section>
      </div>
      <div className="simple_page__footer">
        <ApplicationFooter compact={ false } />
      </div>
    </div>
  );

};

export default DeviceRegistration;